/**
 * NicuPrintLayout — shared black & white A4 print layout for ALL NICU documents
 * (admission slip, bill, payment receipt, reports...).
 *
 * Drop-in compatible with PrintBillLayout — same prop names.
 * Root stays display:none on screen; it only becomes visible while printing.
 *
 * LAYOUT: a flex column that fills the whole A4 sheet —
 *   header pinned to the very top, signatures + footer pinned to the very bottom.
 *
 * READABILITY: screen UIs use small Tailwind sizes (text-xs ≈ 9pt) which look tiny
 * on paper, so the print styles below scale those classes up to proper print sizes.
 *
 * PROPS
 *  className      unique class used for print isolation (must match the element you query before window.print())
 *  title          boxed title, e.g. "NICU Admission Form"
 *  subtitle       small line under the title (optional)
 *  billInfo       JSX — rendered as-is (no grid is forced on it)
 *  table          JSX: <thead>...</thead><tbody>...</tbody>  (NOT a full <table> — it gets wrapped)
 *  summary        JSX: <div><span>Label</span><span>Value</span></div> rows; the LAST row is styled as the total
 *  amountInWords  string, printed in italics under the summary
 *  note           JSX/string, shown in a bordered note box
 *  footer         JSX: custom signature area (default 3-signature block is used when omitted)
 *  signatures     array of labels for the default signature block, e.g. ["Prepared By", "Authorized By"]
 *  isPaid         true → PAID stamp, false → DUE stamp, undefined → no stamp
 *  compact        true → smaller type (use for bills with many rows)
 */
export const NicuPrintLayout = ({
  className = "nicu-print",
  title = "NICU Document",
  subtitle,
  billInfo,
  table,
  summary,
  amountInWords,
  note,
  footer,
  signatures = ["Prepared By", "Authorized By", "Guardian"],
  isPaid,
  compact = false,
}) => {
  // one place to scale the whole document up or down
  const s = compact
    ? { base: "11.5pt", xs: "9.5pt", sm: "11pt", md: "12pt", lg: "14pt", xl: "16pt", x2: "19pt", x3: "22pt", h1: "26pt", title: "13pt", table: "10.5pt", sum: "11.5pt" }
    : { base: "13pt", xs: "11pt", sm: "12.5pt", md: "13.5pt", lg: "15.5pt", xl: "17.5pt", x2: "21pt", x3: "25pt", h1: "30pt", title: "15pt", table: "12pt", sum: "13pt" };

  return (
    <div className={className} style={{ display: "none" }}>
      <style>
        {`
          @media print {
            @page { size: A4; margin: 0; }
            html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }

            /* ---- print isolation ---- */
            body * { visibility: hidden; }
            .print-wrapper, .print-wrapper * { visibility: hidden; }
            .${className}, .${className} * { visibility: visible; }
            .${className} { display: block !important; position: absolute; top: 0; left: 0; right: 0; }
            .${className} * { color: #000 !important; }

            /* ---- page box: header top, footer bottom ---- */
            .np-page {
              box-sizing: border-box;
              width: 100%; min-height: 292mm;
              padding: 11mm 15mm 8mm;
              display: flex; flex-direction: column;
              font-family: Arial, Helvetica, sans-serif;
              font-size: ${s.base}; line-height: 1.5;
              position: relative;
            }
            .np-page::before {
              content: "Mukti Hospital";
              position: absolute; top: 47%; left: 50%;
              transform: translate(-50%, -50%) rotate(-30deg);
              font-size: 46pt; font-weight: 800; white-space: nowrap;
              color: rgba(0,0,0,0.035) !important;
              -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
              z-index: 0;
            }
            .np-page > * { position: relative; z-index: 1; }

            /* ---- scale small screen type up to readable print type ---- */
            .np-page .text-\\[8px\\], .np-page .text-\\[9px\\], .np-page .text-\\[10px\\],
            .np-page [class*="text-["] { font-size: ${s.xs} !important; }
            .np-page .text-xs   { font-size: ${s.xs} !important; }
            .np-page .text-sm   { font-size: ${s.sm} !important; }
            .np-page .text-base { font-size: ${s.md} !important; }
            .np-page .text-lg   { font-size: ${s.lg} !important; }
            .np-page .text-xl   { font-size: ${s.xl} !important; }
            .np-page .text-2xl  { font-size: ${s.x2} !important; }
            .np-page .text-3xl  { font-size: ${s.x3} !important; }
            .np-page p, .np-page span, .np-page td, .np-page th, .np-page div { line-height: 1.5; }

            /* ---- header ---- */
            .np-head { flex: 0 0 auto; text-align: center; padding-bottom: 6px; border-bottom: 3px solid #000; }
            .np-head h1 { margin: 0; font-size: ${s.h1}; font-weight: 800; letter-spacing: 0.5px; }
            .np-head p { margin: 3px 0 0; font-size: ${s.sm}; }

            /* ---- title ---- */
            .np-title { flex: 0 0 auto; text-align: center; margin: 11px 0 13px; }
            .np-title span {
              display: inline-block; border: 2px solid #000;
              padding: 5px 26px; font-size: ${s.title}; font-weight: 800;
              letter-spacing: 3px; text-transform: uppercase;
            }
            .np-sub { margin: 6px 0 0; font-size: ${s.sm}; }

            /* ---- body ---- */
            .np-body { flex: 1 1 auto; }
            .np-info { margin-bottom: 12px; }
            .np-info .full { grid-column: 1 / -1; }

            /* ---- table ---- */
            .np-table table { width: 100%; border-collapse: collapse; font-size: ${s.table}; }
            .np-table th, .np-table td { border: 1px solid #000; padding: 7px 9px; text-align: left; vertical-align: top; }
            .np-table th {
              background: #e6e6e6 !important; font-weight: 700; font-size: ${s.table};
              -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
            }
            .np-table td:last-child, .np-table th:last-child { text-align: right; }

            /* ---- summary ---- */
            .np-summary-wrap { display: flex; justify-content: flex-end; margin-top: 12px; }
            .np-summary { width: 58%; font-size: ${s.sum}; }
            .np-summary > div { display: flex; justify-content: space-between; padding: 5px 10px; border-bottom: 1px dotted #888; }
            .np-summary > div:last-child {
              font-weight: 800; font-size: ${s.lg};
              border-top: 2px solid #000; border-bottom: 2px solid #000; margin-top: 4px;
            }

            .np-words { font-style: italic; font-size: ${s.sm}; margin: 10px 0 0; }
            .np-note { border-left: 4px solid #000; padding: 7px 12px; font-size: ${s.sm}; margin-top: 12px; }

            /* ---- PAID / DUE stamp ---- */
            .np-stamp {
              position: absolute; left: 20mm; bottom: 68mm;
              border: 3px double #000; padding: 4px 20px;
              font-size: 22pt; font-weight: 800; letter-spacing: 4px;
              transform: rotate(-15deg); opacity: 0.5; z-index: 2;
            }

            /* ---- signatures (bottom) ---- */
            .np-sign {
              flex: 0 0 auto; margin-top: auto;
              border-top: 1px dashed #999; padding-top: 7mm;
              display: flex; justify-content: space-between; gap: 12mm;
            }
            .np-sign-item { flex: 1 1 0; text-align: center; }
            .np-sign-space { height: 15mm; }
            .np-sign-line { border-top: 1.5px solid #000; margin-bottom: 6px; }
            .np-sign-label { margin: 0; font-size: ${s.sm}; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; white-space: nowrap; }
            .np-sign-hint { margin: 3px 0 0; font-size: ${s.xs}; }

            /* ---- page footer ---- */
            .np-foot {
              flex: 0 0 auto;
              border-top: 2px solid #000; margin-top: 8px; padding-top: 5px;
              display: flex; justify-content: space-between; font-size: ${s.xs};
            }
          }
        `}
      </style>

      <div className="np-page">
        {/* Header — very top */}
        <div className="np-head">
          <h1>Mukti Hospital</h1>
          <p>Race Course, Sasongacha, Cumilla</p>
          <p>Hotline: +880 1673-625038 &nbsp;|&nbsp; Emergency: 01711-796905</p>
        </div>

        {/* Title */}
        <div className="np-title">
          <span>{title}</span>
          {subtitle && <p className="np-sub">{subtitle}</p>}
        </div>

        {/* Body */}
        <div className="np-body">
          {billInfo && <div className="np-info">{billInfo}</div>}

          {table && (
            <div className="np-table">
              <table>{table}</table>
            </div>
          )}

          {summary && (
            <div className="np-summary-wrap">
              <div className="np-summary">{summary}</div>
            </div>
          )}

          {amountInWords && (
            <p className="np-words"><strong>In Words:</strong> {amountInWords}</p>
          )}

          {note && <div className="np-note">{note}</div>}
        </div>

        {/* PAID / DUE stamp */}
        {isPaid !== undefined && isPaid !== null && (
          <div className="np-stamp">{isPaid ? "PAID" : "DUE"}</div>
        )}

        {/* Signatures — pushed to the bottom */}
        {footer ? (
          <div className="np-sign">{footer}</div>
        ) : (
          <div className="np-sign">
            {signatures.map((label, i) => (
              <div className="np-sign-item" key={i}>
                <div className="np-sign-space" />
                <div className="np-sign-line" />
                <p className="np-sign-label">{label}</p>
                <p className="np-sign-hint">Name &amp; Date</p>
              </div>
            ))}
          </div>
        )}

        {/* Footer — very bottom */}
        <div className="np-foot">
          <span>Mukti Hospital Pvt. Ltd. &nbsp;|&nbsp; muktihospital.com</span>
          <span>Software Powered By: Microters</span>
        </div>
      </div>
    </div>
  );
};

export default NicuPrintLayout;
